import TeamSelectDeprecated from "@/components/messaging/TeamSelect";
import {
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  PrimaryBlueButton,
  SecondaryBlueButton,
  Toggle
} from "@velaro/velaro-component-lib";
import React, { useContext, useEffect, useState } from "react";
import { ProfileContext } from "@velaro/velaro-component-lib";
import { IvrIntegrationModel, IVRVoiceConfiguration } from "../types";
import { WorkflowListItem } from "../Workflows/types";
import LoadingSpinner from "@/components/LoadingSpinner";
import useApi from "@/hooks/useApi";
import IvrVersionHistoryPanel from "./IvrVersionHistoryPanel";
import { SubscriptionsContext } from "@/context/SubscriptionsContext";

interface Props {
  onClose(): void;
  release(): Promise<void>;
  update(integration: IvrIntegrationModel): void;
  integration: IvrIntegrationModel;
  error: string;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function FieldHint({ text }: { text: string }) {
  return (
    <span
      className="ml-1.5 inline-flex items-center justify-center w-4 h-4 rounded-full bg-slate-200 text-slate-500 text-xs cursor-help select-none"
      title={text}
      aria-label={text}
    >
      ?
    </span>
  );
}

function FieldRow({
  label,
  hint,
  children,
  greyed
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
  greyed?: boolean;
}) {
  return (
    <div className={`flex items-start mb-5 ${greyed ? "opacity-50" : ""}`}>
      <div className="w-1/3 pt-1.5 font-semibold text-sm text-slate-700 flex items-center">
        {label}
        {hint && <FieldHint text={hint} />}
      </div>
      <div className="w-2/3">{children}</div>
    </div>
  );
}

function SectionHeader({ title }: { title: string }) {
  return (
    <div className="text-xs font-semibold uppercase tracking-wider text-slate-400 border-b border-slate-100 pb-1 mb-4 mt-6">
      {title}
    </div>
  );
}

// ── Workflow status badge ─────────────────────────────────────────────────────

type WorkflowStatus = "active" | "disabled" | "missing" | "auto";

function workflowStatus(integration: IvrIntegrationModel): WorkflowStatus {
  if (!integration.workflowId) return "auto";
  if (!integration.workflowExists) return "missing";
  if (integration.workflowEnabled === false) return "disabled";
  return "active";
}

function WorkflowStatusBadge({ status }: { status: WorkflowStatus }) {
  const styles: Record<WorkflowStatus, string> = {
    active: "bg-green-50 text-green-700 border border-green-200",
    disabled: "bg-amber-50 text-amber-700 border border-amber-200",
    missing: "bg-red-50 text-red-700 border border-red-200",
    auto: "bg-slate-100 text-slate-500 border border-slate-200"
  };
  const labels: Record<WorkflowStatus, string> = {
    active: "Active",
    disabled: "Disabled",
    missing: "Deleted",
    auto: "Auto-detect"
  };
  return (
    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${styles[status]}`}>
      {labels[status]}
    </span>
  );
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function IvrIntegrationEditModal(props: Props) {
  const [selectedTeam, setSelectedTeam] = useState(props.integration.teamId);
  const [selectedVoiceConfig, setSelectedVoiceConfig] = useState<number | undefined>(
    props.integration.ivrVoiceConfigurationId
  );
  const [selectedWorkflow, setSelectedWorkflow] = useState<number | undefined>(
    props.integration.workflowId
  );
  const [label, setLabel] = useState(props.integration.label ?? "");
  const [fallbackTransferNumber, setFallbackTransferNumber] = useState(
    props.integration.fallbackTransferNumber ?? ""
  );
  const [voiceConfigurations, setVoiceConfigurations] = useState<IVRVoiceConfiguration[]>([]);
  const [workflows, setWorkflows] = useState<WorkflowListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [showVersionHistory, setShowVersionHistory] = useState(false);
  const [enableIvrBridgeCall, setEnableIvrBridgeCall] = useState(false);
  const [bridgeFlagLoading, setBridgeFlagLoading] = useState(false);
  const api = useApi();
  const { profile } = useContext(ProfileContext);
  const { subscription } = useContext(SubscriptionsContext);
  const siteId = subscription?.siteId ?? 0;

  // Velaro staff see superadmin extras (raw IDs, phone SID, etc.)
  const isVelaroStaff = profile?.email?.toLowerCase().endsWith("@velaro.com") ?? false;

  useEffect(() => {
    fetchVoiceConfigurations();
    fetchWorkflows();
    fetchIvrCapabilities();
    if (isVelaroStaff && siteId) fetchIvrFlags();
  }, [isVelaroStaff, siteId]);

  // Fetch bridge-call capability for ALL users (not just staff) so we can grey
  // the bridge number field and show a friendly "not enabled" message.
  async function fetchIvrCapabilities() {
    try {
      const r = await api.admin.get("IVRVoiceConfigurations/Capabilities");
      if (r.ok) {
        const d = await r.json();
        setEnableIvrBridgeCall(d.enableIvrBridgeCall ?? false);
      }
    } catch {}
  }

  async function fetchIvrFlags() {
    try {
      const r = await api.admin.get(`DatabaseTool/IvrFlags/${siteId}`);
      if (r.ok) {
        const d = await r.json();
        setEnableIvrBridgeCall(d.enableIvrBridgeCall ?? false);
      }
    } catch {}
  }

  async function saveIvrBridgeCallFlag(value: boolean) {
    setBridgeFlagLoading(true);
    try {
      await api.admin.put("DatabaseTool/IvrFlags", {
        siteId,
        enableIvrBridgeCall: value
      });
      setEnableIvrBridgeCall(value);
    } catch {}
    finally { setBridgeFlagLoading(false); }
  }

  async function fetchVoiceConfigurations() {
    try {
      const response = await api.messaging.get("IVRVoiceConfigurations");
      const data: IVRVoiceConfiguration[] = await response.json();
      setVoiceConfigurations(Array.isArray(data) ? data : []);
    } catch {
      setVoiceConfigurations([]);
    }
  }

  async function fetchWorkflows() {
    try {
      const response = await api.messaging.get("Workflows/List");
      const data: WorkflowListItem[] = await response.json();
      setWorkflows(Array.isArray(data) ? data : []);
    } catch {
      setWorkflows([]);
    }
  }

  async function handleRelease() {
    setLoading(true);
    await props.release();
    setLoading(false);
  }

  const currentStatus = workflowStatus({
    ...props.integration,
    workflowId: selectedWorkflow,
    workflowExists: selectedWorkflow
      ? (selectedWorkflow === props.integration.workflowId
          ? props.integration.workflowExists
          : true) // optimistically assume new pick exists
      : undefined,
    workflowEnabled: selectedWorkflow === props.integration.workflowId
      ? props.integration.workflowEnabled
      : true
  });

  const selectedWorkflowObj = workflows.find(w => w.id === selectedWorkflow);

  return (
    <Modal show={true} maxWidth="max-w-[780px]">
      {/* Version history side panel */}
      {showVersionHistory && (
        <div className="absolute inset-y-0 right-0 w-96 z-50">
          <IvrVersionHistoryPanel
            integrationId={props.integration.id}
            onRestore={(version) => {
              setSelectedTeam(String(version.teamId));
              setSelectedWorkflow(version.workflowId ?? undefined);
              setSelectedVoiceConfig(version.ivrVoiceConfigurationId ?? undefined);
              setFallbackTransferNumber(version.fallbackTransferNumber ?? "");
              setShowVersionHistory(false);
            }}
            onClose={() => setShowVersionHistory(false)}
          />
        </div>
      )}

      <ModalHeader
        title={`Edit IVR Number — ${props.integration.label ? `${props.integration.label} (${props.integration.phoneNumber})` : props.integration.phoneNumber}`}
        onClose={() => props.onClose()}
      />

      <form
        onSubmit={(e) => {
          e.preventDefault();
          props.update({
            ...props.integration,
            teamId: selectedTeam,
            label: label || undefined,
            ivrVoiceConfigurationId: selectedVoiceConfig,
            workflowId: selectedWorkflow,
            fallbackTransferNumber: fallbackTransferNumber || undefined
          });
        }}
      >
        <ModalBody>
          <div className="p-6 max-h-[70vh] overflow-auto">

            {/* ── Workflow broken warning banner ── */}
            {currentStatus === "disabled" && (
              <div className="mb-5 p-3 rounded-lg bg-amber-50 border border-amber-200 text-sm text-amber-800 flex items-start gap-2">
                <span className="mt-0.5 text-amber-500">⚠</span>
                <div>
                  <strong>Workflow is disabled.</strong> Callers will still reach this number
                  but the assigned workflow (<em>{props.integration.workflowName}</em>) is
                  turned off. Turn the workflow back on in{" "}
                  <strong>Messaging → Workflows</strong>, or reassign to a different workflow
                  below.
                </div>
              </div>
            )}
            {currentStatus === "missing" && (
              <div className="mb-5 p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-800 flex items-start gap-2">
                <span className="mt-0.5 text-red-500">✕</span>
                <div>
                  <strong>Assigned workflow was deleted.</strong> This number has no automation
                  and callers will hear a message that the service is offline. Please assign a
                  new workflow below.
                </div>
              </div>
            )}
            {!selectedWorkflow && (
              <div className="mb-5 p-3 rounded-lg bg-blue-50 border border-blue-200 text-sm text-blue-700 flex items-start gap-2">
                <span className="mt-0.5">ℹ</span>
                <div>
                  No workflow assigned. The system will use the <strong>site default workflow</strong>{" "}
                  (if configured) or auto-detect a matching workflow. For reliable routing,
                  assign an explicit workflow below.
                </div>
              </div>
            )}

            <SectionHeader title="Phone Number" />

            <FieldRow
              label="Number"
              hint="This is your Twilio phone number. It cannot be changed — release and purchase a new one if needed."
              greyed
            >
              <div className="flex items-center gap-2">
                <span className="text-slate-700 font-mono text-sm bg-slate-50 border border-slate-200 rounded px-3 py-1.5">
                  {props.integration.phoneNumber}
                </span>
                <span className="text-xs text-slate-400">Read-only</span>
              </div>
            </FieldRow>

            <FieldRow
              label="Label"
              hint="A friendly name for this number shown in the table, e.g. 'Main Inbound', 'Sales Line', 'Trunk #1'."
            >
              <input
                type="text"
                className="input w-full"
                placeholder="e.g. Main Inbound Number"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                maxLength={100}
              />
            </FieldRow>

            <SectionHeader title="Routing" />

            <FieldRow
              label="Team"
              hint="The team that receives conversations from this IVR number."
            >
              <TeamSelectDeprecated
                id="ivr-team"
                className="input w-full"
                onChange={(e) => setSelectedTeam(e.currentTarget.value)}
                value={selectedTeam}
              />
            </FieldRow>

            <FieldRow
              label="Workflow"
              hint="The automation workflow that runs when a call arrives on this number. Disabling or deleting the workflow here will not break the number — but callers won't get automated responses."
            >
              <div className="flex items-center gap-2">
                <select
                  id="ivr-workflow"
                  className="input flex-1"
                  onChange={(e) =>
                    setSelectedWorkflow(
                      e.currentTarget.value ? Number(e.currentTarget.value) : undefined
                    )
                  }
                  value={selectedWorkflow ?? ""}
                >
                  <option value="">— Use site default —</option>
                  {workflows.map((workflow) => (
                    <option key={workflow.id} value={workflow.id}>
                      {workflow.name}
                    </option>
                  ))}
                </select>
                <WorkflowStatusBadge status={currentStatus} />
              </div>
              {selectedWorkflow && selectedWorkflowObj && (
                <p className="mt-1 text-xs text-slate-400">
                  Workflow #{selectedWorkflow} · Disabling it in the workflow editor will NOT
                  break this number — it runs regardless of its enabled state.
                </p>
              )}
            </FieldRow>

            <SectionHeader title="Voice & Transcription" />

            <FieldRow
              label="Voice Configuration"
              hint="Controls the AI voice (ElevenLabs), speech recognition provider, and silence detection settings for this number."
            >
              {voiceConfigurations.length === 0 ? (
                <p className="text-sm text-slate-500">
                  No voice configurations yet.{" "}
                  <span className="text-cornflower-blue-600">
                    Create one in the Voice Configurations section below.
                  </span>
                </p>
              ) : (
                <select
                  id="ivr-voice-config"
                  className="input w-full"
                  onChange={(e) =>
                    setSelectedVoiceConfig(
                      e.currentTarget.value ? Number(e.currentTarget.value) : undefined
                    )
                  }
                  value={selectedVoiceConfig ?? ""}
                >
                  <option value="">— Use site default voice —</option>
                  {voiceConfigurations.map((config) => (
                    <option key={config.id} value={config.id}>
                      {config.name}
                    </option>
                  ))}
                </select>
              )}
            </FieldRow>

            <SectionHeader title="Agent Bridge & Transfer" />

            {!enableIvrBridgeCall && (
              <div className="mb-4 p-3 bg-slate-50 border border-slate-200 rounded-lg flex items-start gap-3">
                <span className="text-slate-400 mt-0.5 flex-shrink-0">🔒</span>
                <div>
                  <p className="text-sm font-medium text-slate-600">Live agent transfer is not enabled on your plan.</p>
                  <p className="text-xs text-slate-400 mt-0.5">Contact Velaro support to activate IVR Bridge Call and allow callers to connect directly with your team.</p>
                </div>
              </div>
            )}

            <FieldRow
              label="Agent Phone Number"
              hint="Callers will be connected to this number when they ask to speak with a live person. Use E.164 format, e.g. +18005551234."
              greyed={!enableIvrBridgeCall}
            >
              <input
                type="tel"
                className="input w-full"
                placeholder="+18005551234"
                value={fallbackTransferNumber}
                disabled={!enableIvrBridgeCall}
                onChange={(e) => setFallbackTransferNumber(e.target.value)}
                pattern="^\+?[1-9]\d{1,14}$|^$"
                title="E.164 format recommended, e.g. +18005551234"
              />
              {enableIvrBridgeCall && (
                <p className="mt-1 text-xs text-slate-400">
                  When a caller asks to speak with an agent, the call is connected here.
                  Also used as a fallback if the call flow cannot resolve the customer's question.
                </p>
              )}
            </FieldRow>

            {/* ── Velaro Staff / Superadmin section ── */}
            {isVelaroStaff && (
              <>
                <SectionHeader title="⚡ Velaro Staff Only" />
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 space-y-3">
                  <p className="text-xs text-amber-700 font-medium mb-3">
                    These fields are visible to @velaro.com accounts only. Handle with care.
                  </p>

                  <div className="flex items-start gap-4 text-sm">
                    <div className="w-1/3 text-slate-600 font-medium pt-0.5">Integration ID</div>
                    <div className="font-mono text-slate-700">{props.integration.id}</div>
                  </div>

                  <div className="flex items-start gap-4 text-sm">
                    <div className="w-1/3 text-slate-600 font-medium pt-0.5">
                      Raw Workflow ID
                      <FieldHint text="The numeric ID stored in TwilioIntegrations.WorkflowId. Use the dropdown above to change it — or update the DB directly if the dropdown list is empty." />
                    </div>
                    <div className="font-mono text-slate-700">
                      {selectedWorkflow ?? <span className="text-slate-400">null</span>}
                    </div>
                  </div>

                  <div className="flex items-center justify-between text-sm pt-1 border-t border-amber-200 mt-2">
                    <div>
                      <div className="text-slate-700 font-medium">IVR Bridge Call</div>
                      <div className="text-xs text-amber-600">
                        Subscription flag — enables the <code>bridge_call_to_agent</code> bot skill.
                        Requires a Bridge / Agent Phone Number to be set above.
                      </div>
                    </div>
                    {bridgeFlagLoading
                      ? <span className="text-xs text-slate-400">Saving…</span>
                      : <Toggle
                          toggled={enableIvrBridgeCall}
                          changeToggle={() => saveIvrBridgeCallFlag(!enableIvrBridgeCall)}
                        />
                    }
                  </div>
                </div>
              </>
            )}

            {props.error && (
              <div className="text-red-600 text-sm mt-3">{props.error}</div>
            )}
          </div>
        </ModalBody>

        <ModalFooter>
          <div className="p-4 flex items-center justify-between">
            {/* Left: version history */}
            <SecondaryBlueButton
              type="button"
              label="Version History"
              onClick={() => setShowVersionHistory(true)}
            />

            <div className="flex items-center gap-2">
              {loading ? (
                <span className="text-sm text-slate-500 flex items-center gap-1">
                  Saving… <LoadingSpinner />
                </span>
              ) : (
                <>
                  <button
                    type="button"
                    className="btn ml-2 bg-red-500 hover:bg-red-600 text-white py-2 px-4 rounded-md text-sm font-medium"
                    onClick={handleRelease}
                  >
                    Release Number
                  </button>
                  <PrimaryBlueButton
                    type="submit"
                    label="Save Changes"
                    disabled={loading}
                  />
                </>
              )}
            </div>
          </div>
        </ModalFooter>
      </form>
    </Modal>
  );
}
